/**
 * useSmartPolling Hook
 *
 * A smart polling hook that:
 * - Pauses when the browser tab is hidden (saves server resources)
 * - Optionally pauses when user is idle
 * - Triggers immediate refresh when tab becomes visible
 * - Supports adaptive backoff (optional)
 * - Uses centralized polling configuration with presets from env/GrowthBook
 *
 * Available Presets:
 * - DEFAULT: General purpose (10s, from env)
 * - FLOW_LOGS: Real-time log viewing (10s)
 * - LIVE_SAMPLES: Data arrival checking (20s)
 * - PIPELINE_LIST: Dashboard refresh (30s)
 * - UI_TIME_DISPLAY: Client-side updates (10s)
 * - SCANNING: Detection workflows (5s)
 *
 * Usage:
 * ```tsx
 * // Using a preset
 * useSmartPolling({
 *   callback: async () => await fetchLogs(),
 *   preset: 'FLOW_LOGS',
 *   enabled: true,
 * });
 *
 * // Using custom interval (overrides preset)
 * useSmartPolling({
 *   callback: async () => await fetchData(),
 *   intervalOverride: 15000,
 *   enabled: true,
 * });
 * ```
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import usePageVisibility from 'js/hooks/usePageVisibility';
import { usePollingConfig, PollingPreset } from './usePollingConfig';
import { useUserIdle } from './useUserIdle';

/**
 * Delay before refreshing when tab becomes visible.
 * This prevents race conditions with other visibility change handlers
 * that may also trigger data fetches.
 */
const VISIBILITY_REFRESH_DELAY_MS = 100;

export interface UseSmartPollingOptions {
  /**
   * Callback function to execute on each poll
   * Can be async - the hook will handle it properly
   */
  callback: () => void | Promise<void>;

  /**
   * Polling preset to use (determines default interval and behavior)
   * Available presets:
   * - DEFAULT: General purpose (10s, from env)
   * - FLOW_LOGS: Real-time log viewing (10s)
   * - LIVE_SAMPLES: Data arrival checking (20s)
   * - PIPELINE_LIST: Dashboard refresh (30s)
   * - UI_TIME_DISPLAY: Client-side updates (10s)
   * - SCANNING: Detection workflows (5s)
   * @default 'DEFAULT'
   */
  preset?: PollingPreset;

  /**
   * Override the polling interval from config/preset
   * If provided, this takes precedence over the preset config
   */
  intervalOverride?: number;

  /**
   * Master on/off switch for polling
   * @default true
   */
  enabled?: boolean;

  /**
   * Override pauseOnHidden from preset config
   * If not provided, uses preset config value
   */
  pauseOnHidden?: boolean;

  /**
   * Override refreshOnVisible from preset config
   * If not provided, uses preset config value
   */
  refreshOnVisible?: boolean;

  /**
   * Override pauseOnIdle from preset config
   * If not provided, uses preset config value
   */
  pauseOnIdle?: boolean;

  /**
   * Override idleTimeoutMs from preset config
   * If not provided, uses preset config value
   */
  idleTimeoutMs?: number;

  /**
   * Whether to trigger immediately on mount
   * @default false - interval-based polling starts after first interval
   */
  triggerOnMount?: boolean;
}

export interface UseSmartPollingReturn {
  /**
   * Whether polling is currently active
   */
  isPolling: boolean;

  /**
   * Whether the tab is currently visible
   */
  isVisible: boolean;

  /**
   * Whether the user is currently idle
   */
  isIdle: boolean;

  /**
   * Manually trigger the callback
   */
  triggerNow: () => void;

  /**
   * Current effective polling interval
   */
  currentInterval: number;

  /**
   * The polling preset being used
   */
  currentPreset: PollingPreset;
}

/**
 * Smart polling hook with visibility and idle awareness
 */
export function useSmartPolling(options: UseSmartPollingOptions): UseSmartPollingReturn {
  const {
    callback,
    preset = 'DEFAULT',
    intervalOverride,
    enabled = true,
    pauseOnHidden: pauseOnHiddenOverride,
    refreshOnVisible: refreshOnVisibleOverride,
    pauseOnIdle: pauseOnIdleOverride,
    idleTimeoutMs: idleTimeoutMsOverride,
    triggerOnMount = false,
  } = options;

  // Get polling configuration for the specified preset
  const config = usePollingConfig(preset);

  // Resolve final values (options override config)
  const pauseOnHidden = pauseOnHiddenOverride ?? config.pauseOnHidden;
  const refreshOnVisible = refreshOnVisibleOverride ?? config.refreshOnVisible;
  const pauseOnIdle = pauseOnIdleOverride ?? config.pauseOnIdle;
  const idleTimeoutMs = idleTimeoutMsOverride ?? config.idleTimeoutMs;
  const interval = intervalOverride ?? config.intervalMs;

  // Track visibility and idle state
  const isVisible = usePageVisibility();
  const isIdle = useUserIdle(idleTimeoutMs, pauseOnIdle);

  // Refs to track state across renders
  const callbackRef = useRef(callback);
  const isExecutingRef = useRef(false);
  const previousVisibleRef = useRef<boolean | null>(null); // null = first render
  const intervalTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasMountedRef = useRef(false);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Memoized trigger function
  const triggerNow = useCallback(async () => {
    if (isExecutingRef.current) return;

    isExecutingRef.current = true;
    try {
      await callbackRef.current();
    } catch (error) {
      // Log error but don't crash - consumers can add their own error handling
      console.error('Smart polling callback error:', error);
    } finally {
      isExecutingRef.current = false;
    }
  }, []);

  // Determine if polling should be active
  const shouldPoll = useMemo(() => {
    if (!enabled) return false;
    if (pauseOnHidden && !isVisible) return false;
    if (pauseOnIdle && isIdle) return false;
    return true;
  }, [enabled, pauseOnHidden, isVisible, pauseOnIdle, isIdle]);

  // Helper to clear and restart the interval timer
  const resetIntervalTimer = useCallback(() => {
    if (intervalTimerRef.current) {
      clearInterval(intervalTimerRef.current);
      intervalTimerRef.current = null;
    }

    if (shouldPoll) {
      intervalTimerRef.current = setInterval(() => {
        triggerNow();
      }, interval);
    }
  }, [shouldPoll, interval, triggerNow]);

  // Handle visibility change - immediate refresh when tab becomes visible
  useEffect(() => {
    // Skip on first render - previousVisibleRef is null
    if (previousVisibleRef.current === null) {
      previousVisibleRef.current = isVisible;
      return;
    }

    const wasHidden = !previousVisibleRef.current;
    const isNowVisible = isVisible;

    // Update ref AFTER reading it to avoid race conditions
    previousVisibleRef.current = isVisible;

    // If transitioning from hidden to visible and refresh is enabled
    if (wasHidden && isNowVisible && refreshOnVisible && enabled) {
      // Delay to prevent race conditions with other visibility handlers
      const timer = setTimeout(() => {
        triggerNow();
        // Reset the interval timer to prevent double execution
        // (e.g., if visibility changes at 9.9s and interval fires at 10s)
        resetIntervalTimer();
      }, VISIBILITY_REFRESH_DELAY_MS);

      return () => clearTimeout(timer);
    }
  }, [isVisible, refreshOnVisible, enabled, triggerNow, resetIntervalTimer]);

  // Trigger on mount if enabled
  useEffect(() => {
    if (triggerOnMount && enabled && !hasMountedRef.current) {
      hasMountedRef.current = true;
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        triggerNow();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [triggerOnMount, enabled, triggerNow]);

  // Main polling effect
  useEffect(() => {
    if (!shouldPoll) {
      // Clear timer when polling should stop
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
      return;
    }

    // Set up interval
    intervalTimerRef.current = setInterval(() => {
      triggerNow();
    }, interval);

    // Cleanup
    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
    };
  }, [shouldPoll, interval, triggerNow]);

  return {
    isPolling: shouldPoll,
    isVisible,
    isIdle,
    triggerNow,
    currentInterval: interval,
    currentPreset: preset,
  };
}

export default useSmartPolling;
